// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { User, UserLogonState } from '@interfaces/user.interfaces';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { passwordMatch, passwordPattern } from '../../utils/form-utils';
import { AuthService } from '../services/auth.service';
import { LoginService } from '../services/login.service';
import { ViewportScroller } from '@angular/common';
import { SuccessModalService } from '@services/success-modal.service';
import { MatDialog } from '@angular/material/dialog';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { AppEventEx } from '@interfaces/interfaces';
import { AuthenticationStatus } from '@interfaces/auth.interfaces';
import { SnackbarService } from '@services/snackbar.service';

@Component({
    selector: 'app-new-password',
    templateUrl: './new-password.component.html',
    styleUrls: ['./new-password.component.scss'],
})
export class NewPasswordComponent implements OnInit, OnDestroy {
    isComponentAlive = true;
    isLoading = false;
    newPasswordForm: FormGroup;
    error: string = null;
    onLogin = true;
    phone: string = '';
    viewPassword = false;
    viewPassword2 = false;
    passwordRequirements = {
        hasLower: false,
        hasUpper: false,
        hasNumber: false,
        hasSpecialChar: false,
        hasMinLength: false,
        bothMatch: false,
    };

    isVerifyShowing = false;

    constructor(
        private readonly authService: AuthService,
        private readonly loginService: LoginService,
        private readonly router: Router,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly viewportScroller: ViewportScroller,
        private readonly successModalService: SuccessModalService,
        private readonly snackbarService: SnackbarService,
        private readonly dialog: MatDialog
    ) {
        if (!this.loginService.getUsername() && !this.loginService.getResetToken() && !this.loginService.getTempPassword()) {
            this.router.navigate(['login', 'reset-password']);
            return;
        }

        if (!window.location.pathname.includes('login')) {
            this.onLogin = false;
        }
    }

    ngOnInit(): void {
        if (!this.onSettingsPage()) {
            this.navService.setTitle('Create New Password');
        }

        this.buildForm();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    buildForm(): void {
        this.newPasswordForm = new FormGroup(
            {
                password: new FormControl('', [Validators.required, Validators.pattern(passwordPattern)]),
                password2: new FormControl('', [Validators.required]),
            },
            { validators: passwordMatch }
        );
    }

    determineRoute(user: User): void {
        this.isLoading = false;
        if (user?.sendToWelcomeFlow) {
            this.router.navigate(['/welcome']);
        } else {
            this.navService.accountType = null;
            this.userService.logOut();
            this.router.navigate(['/login']);
        }
    }

    newPasswordSubmit(): void {
        const { password, password2 } = this.newPasswordForm.value;

        if (password !== password2) {
            this.newPasswordForm.get('password2').setErrors({ password: 'not equal' });
            return;
        }

        if (this.newPasswordForm.valid) {
            this.isLoading = true;
            const password = this.newPasswordForm.value.password;
            // set password for new user vs reset/forgot
            if (this.loginService.getResetToken()) {
                this.authService.confirmResetPassword(
                    this.loginService.getEmail(),
                    this.loginService.getResetToken(),
                    password,
                    () => this.newPasswordSubmitSuccess(),
                    () => this.newPasswordSubmitFailure('reset-token')
                );
            } else {
                this.authService.completeNewPasswordChallenge(
                    password,
                    () => this.newPasswordSubmitSuccess(),
                    () => this.newPasswordSubmitFailure('first-login'),
                    (state) => this.mfaCallback(state.phoneValidation)
                );
            }
        }
    }

    scrollToTop() {
        this.viewportScroller.scrollToPosition([0, 0]);
    }

    mfaCallback(phone: string): void {
        this.scrollToTop();
        this.phone = phone;
        this.isVerifyShowing = true;
        this.isLoading = false;
    }

    authenticateTokenFailure(): void {
        this.authenticateFailure();
    }

    authenticateFailure(): void {
        //update the last logon date here
        if (this.userService.user) {
            const user = this.userService.user;
            this.loginService.logLoginState(user.userId, user.logonId, UserLogonState.AuthenticationFailure).subscribe();
        }
        this.isLoading = false;
    }

    authenticateTokenSuccess(user: User): void {
        this.isLoading = false;
        // mfa succeeded: the user is logged in, has auth tokens, and can progress into the app.
        this.userService.setUser(user);
        this.determineRoute(user);
    }

    async newPasswordSubmitFailure(result: 'reset-token' | 'first-login') {
        this.isLoading = false;
        if (result === 'reset-token') {
            await this.router.navigate(['login', 'reset-token']);
        }
    }

    newPasswordSubmitSuccess() {
        this.loginService.setResetToken(null);
        this.loginService.setTempPassword(null);

        if (!this.userService.user?.sendToWelcomeFlow) {
            this.isLoading = false;
            this.snackbarService.openSnackbar('success', 'Your password has been updated, and you can now use it to log in.');
            this.determineRoute(this.userService.user);
        } else {
            this.determineRoute(this.userService.user);
        }
    }

    verifyChange(event: AppEventEx<AuthenticationStatus>) {
        if (event.context == 'success') {
            this.authenticateTokenSuccess(event.data.user);
        } else if (event.context == 'newToken') {
            this.successModalService.showSuccess({
                title: 'Verification Code Sent',
                content:
                    'A new verification code has been sent. Please close this window and enter the new code. If you do not receive the code within 5 minutes, click “Request New Code” to start over. Note: the verification code is only valid for 5 minutes.',
            });
            const password = this.newPasswordForm.value.password;

            const user = this.userService.user;
            this.loginService.authenticateUser(
                this.userService.user.logonId,
                password,
                user?.phone,
                () => {
                    this.isLoading = false;
                    this.determineRoute(this.userService.user);
                },
                () => {},
                (state) => {
                    this.isLoading = false;
                    this.mfaCallback(state.phoneValidation);
                }
            );
        }
    }

    public onSettingsPage(): boolean {
        if (this.router.url.includes('dashboard') || this.router.url.includes('main')) {
            return true;
        }

        return false;
    }

    public passwordVisible(inputField: number): void {
        const field = inputField === 1 ? 'viewPassword' : 'viewPassword2';
        this[field] = !this[field];
    }
}
